import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import type { OffersStage } from "@/components/pages/analytics/front-desk/components/offers/types";
import { STAGE_TO_SURFACE } from "@/components/pages/analytics/front-desk/hooks/useOffersAnalytics";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "@/components/pages/analytics/front-desk/utils/analyticsDateUtils";
import { analyticsOffersService } from "@/services/analytics-offers-service";
import { analyticsTenantService } from "@/services/analytics-tenant-service";
import { partnerDashboardService } from "@/services/partner-dashboard-service";
import type {
  RevenueTrendResponse,
  SurfaceTabResponse,
} from "@/types/analytics-offers";
import {
  buildAdminOffersGroupSections,
  buildAdminRoomUpgradeSection,
  mapPerHotelTrendsToChartData,
  type AdminOffersRevenueTrendData,
  type AdminOffersSectionData,
} from "../utils/adminOffersMappers";
import { useAdminTenantId } from "./useAdminTenantId";

function isDateRangeReady(dateRange: DateRange): boolean {
  return Boolean(
    toAnalyticsDateParam(dateRange.from) && toAnalyticsDateParam(dateRange.to),
  );
}

async function fetchHotelSurfaceTab(
  hotelId: string,
  from: string,
  to: string,
  surface: (typeof STAGE_TO_SURFACE)[OffersStage],
): Promise<SurfaceTabResponse | null> {
  try {
    return await analyticsOffersService.getSurfaceTab({
      hotelId,
      from,
      to,
      surface,
    });
  } catch {
    return null;
  }
}

async function fetchHotelRevenueTrend(
  hotelId: string,
  from: string,
  to: string,
): Promise<RevenueTrendResponse | null> {
  try {
    return await analyticsOffersService.getRoomUpgradeRevenueTrend({
      hotelId,
      from,
      to,
    });
  } catch {
    return null;
  }
}

async function fetchAdminOffersData(
  tenantId: string,
  from: string,
  to: string,
  previousFrom: string,
  previousTo: string,
  stage: OffersStage,
) {
  const surface = STAGE_TO_SURFACE[stage];
  const fetchRoomUpgrade = stage === "preCheckIn";

  const offersTable = await analyticsTenantService.getOffersTable({ from, to });
  const hotels = offersTable.rows;

  const [
    surfaceTabs,
    previousSurfaceTabs,
    hotelOptions,
    roomUpgradeSummary,
    previousRoomUpgradeSummary,
    revenueTrends,
  ] = await Promise.all([
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelSurfaceTab(hotel.hotelId, from, to, surface),
      ),
    ),
    Promise.all(
      hotels.map((hotel) =>
        fetchHotelSurfaceTab(hotel.hotelId, previousFrom, previousTo, surface),
      ),
    ),
    partnerDashboardService
      .getHotelFilterOptions(tenantId)
      .catch(
        () =>
          [] as Awaited<
            ReturnType<typeof partnerDashboardService.getHotelFilterOptions>
          >,
      ),
    fetchRoomUpgrade
      ? analyticsTenantService.getOffersRoomUpgradeSummary({ from, to })
      : Promise.resolve(null),
    fetchRoomUpgrade
      ? analyticsTenantService.getOffersRoomUpgradeSummary({
          from: previousFrom,
          to: previousTo,
        })
      : Promise.resolve(null),
    fetchRoomUpgrade
      ? Promise.all(
          hotels.map((hotel) =>
            fetchHotelRevenueTrend(hotel.hotelId, from, to),
          ),
        )
      : Promise.resolve([] as Array<RevenueTrendResponse | null>),
  ]);

  const hotelLogosById = Object.fromEntries(
    hotelOptions.map((hotel) => [hotel.id, hotel.logoId]),
  );

  const currentSurfaceByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, surfaceTabs[index]]),
  );

  const previousSurfaceByHotelId = new Map(
    hotels.map((hotel, index) => [hotel.hotelId, previousSurfaceTabs[index]]),
  );

  const trendsByHotelId = new Map(
    hotels.map((hotel, index) => [
      hotel.hotelId,
      fetchRoomUpgrade ? revenueTrends[index] : null,
    ]),
  );

  const roomUpgradeSection = fetchRoomUpgrade
    ? buildAdminRoomUpgradeSection(
        hotels,
        currentSurfaceByHotelId,
        previousSurfaceByHotelId,
        hotelLogosById,
        roomUpgradeSummary,
        previousRoomUpgradeSummary,
      )
    : null;

  const groupSections = buildAdminOffersGroupSections(
    hotels,
    currentSurfaceByHotelId,
    previousSurfaceByHotelId,
    hotelLogosById,
    stage,
  );

  const revenueTrend = fetchRoomUpgrade
    ? mapPerHotelTrendsToChartData(hotels, trendsByHotelId)
    : null;

  return {
    roomUpgradeSection,
    groupSections,
    revenueTrend,
  };
}

export const adminOffersKeys = {
  all: ["admin-analytics-offers"] as const,
  hotels: (tenantId: string, from: string, to: string, stage: OffersStage) =>
    [...adminOffersKeys.all, tenantId, from, to, stage] as const,
};

export function useAdminOffersHotels(dateRange: DateRange, stage: OffersStage) {
  const tenantId = useAdminTenantId();
  const from = toAnalyticsDateParam(dateRange.from) ?? "";
  const to = toAnalyticsDateParam(dateRange.to) ?? "";
  const previousRange = getPreviousAnalyticsDateRange(dateRange);
  const previousFrom = toAnalyticsDateParam(previousRange?.from) ?? "";
  const previousTo = toAnalyticsDateParam(previousRange?.to) ?? "";
  const isReady = Boolean(
    tenantId && isDateRangeReady(dateRange) && previousFrom && previousTo,
  );

  const query = useQuery({
    queryKey: adminOffersKeys.hotels(tenantId ?? "", from, to, stage),
    queryFn: () =>
      fetchAdminOffersData(
        tenantId!,
        from,
        to,
        previousFrom,
        previousTo,
        stage,
      ),
    enabled: isReady,
  });

  const roomUpgradeSection = useMemo(
    () => query.data?.roomUpgradeSection ?? null,
    [query.data?.roomUpgradeSection],
  );

  const groupSections = useMemo<AdminOffersSectionData[]>(
    () => query.data?.groupSections ?? [],
    [query.data?.groupSections],
  );

  const revenueTrend = useMemo<AdminOffersRevenueTrendData | null>(
    () => query.data?.revenueTrend ?? null,
    [query.data?.revenueTrend],
  );

  return {
    tenantId,
    roomUpgradeSection,
    groupSections,
    revenueTrend,
    isLoading: query.isLoading,
    isError: query.isError,
    isReady,
  };
}
